import React, { useState } from "react";
import AskAddyV2 from "../../models/askAddyV2";
import { buildJob } from "./autopilotFormat";
import useAsyncAction from "./useAsyncAction";

const DEFAULT_TIME = "08:00";
// "Every hour" runs as a heartbeat; the v2 server allows one every 15 minutes or more.
const HOURLY_MINUTES = 60;
const MAX_NAME_LENGTH = 80;
const NAME_WORDS = 6;

const TIMINGS = [
  { value: "weekdays", label: "Weekdays" },
  { value: "daily", label: "Every day" },
  { value: "heartbeat", label: "Every hour" },
  { value: "event", label: "When this loan changes", needsLoan: true },
];

const getLocalTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    return undefined;
  }
};

/** The task's name, from what it does: its first few words. */
const nameFrom = (prompt) => {
  const words = prompt.trim().replace(/\s+/g, " ").split(" ");
  const name = words.slice(0, NAME_WORDS).join(" ").replace(/[.,;:!?]+$/, "");

  return (words.length > NAME_WORDS ? `${name}…` : name).slice(0, MAX_NAME_LENGTH);
};

/**
 * A new scheduled task: what to do, and when. A quick start fills both in (`initial`); on a loan
 * it can watch just that loan.
 */
const NewJobForm = ({ identity, loanId, assistantName, initial, onCreated, onCancel }) => {
  const [prompt, setPrompt] = useState(initial?.prompt || "");
  const [timing, setTiming] = useState(initial?.timing || "weekdays");
  const [time, setTime] = useState(initial?.time || DEFAULT_TIME);
  const [isThisLoan, setIsThisLoan] = useState(!!loanId);
  const { isBusy, notice, run } = useAsyncAction(onCreated);
  const timings = TIMINGS.filter(({ needsLoan }) => !needsLoan || loanId);
  const isAtTime = timing === "weekdays" || timing === "daily";

  const handleSubmit = (event) => {
    event.preventDefault();
    const job = buildJob({
      name: initial?.name && prompt === initial.prompt ? initial.name : nameFrom(prompt),
      prompt: prompt.trim(),
      timing,
      time,
      everyMinutes: HOURLY_MINUTES,
      loanId,
      isThisLoan,
      timeZone: getLocalTimeZone(),
    });
    run(() => AskAddyV2.autopilot.createJob(job, identity));
  };

  return (
    <form className="lendy-profile-form" onSubmit={handleSubmit}>
      <label>
        What should {assistantName} do?
        <textarea
          required
          autoFocus
          rows={3}
          value={prompt}
          placeholder="Tell me which loans need my attention today."
          onChange={(event) => setPrompt(event.target.value)}
        />
      </label>
      <div className="lendy-profile-form-field" role="group" aria-label="When">
        <span className="lendy-profile-form-label">When</span>
        <div className="lendy-profile-segments">
          {timings.map(({ value, label }) => (
            <button key={value} type="button" aria-pressed={timing === value} onClick={() => setTiming(value)}>
              {label}
            </button>
          ))}
        </div>
        {isAtTime && (
          <input type="time" required aria-label="Time" value={time} onChange={(event) => setTime(event.target.value)} />
        )}
      </div>
      {loanId && timing !== "event" && (
        <label className="lendy-profile-check">
          <input type="checkbox" checked={isThisLoan} onChange={(event) => setIsThisLoan(event.target.checked)} />
          Only this loan
        </label>
      )}
      <div className="lendy-profile-replies">
        <button type="submit" className="lendy-pill-button lendy-pill-button--go" disabled={isBusy || !prompt.trim()}>
          Create task
        </button>
        <button type="button" className="lendy-btn lendy-btn--text" onClick={onCancel}>
          Cancel
        </button>
      </div>
      {notice && <p className="lendy-profile-note lendy-profile-note--error">{notice}</p>}
    </form>
  );
};

export default NewJobForm;
